import React, {
  useContext,
  useEffect,
  useState,
  useRef,
  Suspense,
  lazy,
} from 'react';
import { Card, Flex, Spin } from 'antd';
import Legend from './Legend';
import { useTheme } from '../../context/ThemeContext';
import {
  CHART_BG_COLOR,
  CHART_MARGINS,
  DARK,
  VEHICLE_STATUS,
} from '../../utilities/constants';
import { API } from '../../services';
import { ErrorMessageContext } from '../../context/ErrorMessageContext';
import useApiPolling from '../../hooks/useApiPolling';
import { SuspenseFallback } from '../../components/SuspenseFallback/SuspenseFallback';
import { getErrorMessage } from '../../utilities/commonFunctions';

// Lazy load the Plot component
const Plot = lazy(() => import('../../components/Plot/Plot'));

const VehicleStatusChart = () => {
  const { theme } = useTheme();
  const { addErrorMessage } = useContext(ErrorMessageContext);
  const [chartDataLoading, setChartDataLoading] = useState(true);
  const isFirstCall = useRef(true);

  const [totalCount, setTotalCount] = useState(0);
  const initialPlotData = {
    values: [0, 0],
    labels: [VEHICLE_STATUS.Active, VEHICLE_STATUS.Inactive],
    type: 'pie',
    hole: 0.75,
    textinfo: 'value',
    textposition: 'outside',
    textfont: {
      size: 16,
      color: theme === DARK ? '#FFFFFF' : '#000000',
      weigth: 700,
    },
    automargin: true,
    marker: {
      colors: ['#1DAA00', '#007BC0'],
    },
  };

  const initialLayout = {
    showlegend: false,
    paper_bgcolor: CHART_BG_COLOR,
    plot_bgcolor: CHART_BG_COLOR,
    margin: CHART_MARGINS,
    height: 230,
    width: 230,
    annotations: [
      {
        font: {
          color: theme === DARK ? '#FFFFFF' : '#000000',
          size: 16,
        },
        showarrow: false,
        text: `<b>${totalCount || 0}</b><br>Total vehicles`,
        x: 0.5,
        y: 0.5,
      },
    ],
  };
  const [plotData, setPlotData] = useState([initialPlotData]);
  const [layout, setLayout] = useState(initialLayout);

  useEffect(() => {
    setLayout(prev => ({
      ...prev,
      annotations: prev.annotations.map(annotation => ({
        ...annotation,
        text: `<b>${totalCount || 0}</b><br>Total vehicles`,
      })),
    }));
  }, [totalCount]);

  useEffect(() => {
    setPlotData(prev =>
      prev.map(item => ({
        ...item,
        textfont: {
          ...item.textfont,
          color: theme === DARK ? '#FFFFFF' : '#000000',
        },
      }))
    );
    setLayout(prev => ({
      ...prev,
      annotations: prev.annotations.map(annotation => ({
        ...annotation,
        font: {
          color: theme === DARK ? '#FFFFFF' : '#000000',
          size: 16,
        },
      })),
    }));
  }, [theme]);

  const getVehicleStatusChartData = async () => {
    setChartDataLoading(isFirstCall.current && true);
    try {
      const { data } = await API.getVehicleStatusChartData(false);
      setPlotData(prev =>
        prev.map(plot => ({
          ...plot,
          values: [data?.Active, data?.Inactive],
        }))
      );
      setTotalCount(data?.Count);
    } catch (error) {
      if (isFirstCall.current) {
        addErrorMessage(
          getErrorMessage(
            error,
            'Unable to fetch vehicle status data! Please try again after sometime.'
          )
        );
      }
    } finally {
      isFirstCall.current = false;
      setChartDataLoading(false);
    }
  };

  useApiPolling(getVehicleStatusChartData);

  return (
    <Spin
      spinning={chartDataLoading}
      size="large"
      className="overview-chart-spinner"
    >
      <Card
        size="small"
        title="Vehicle Status"
        variant="borderless"
        className="full-height"
      >
        <Flex vertical gap={56}>
          <Legend
            items={[
              { key: 'active', label: VEHICLE_STATUS.Active, color: '#1DAA00' },
              {
                key: 'inactive',
                label: VEHICLE_STATUS.Inactive,
                color: '#007BC0',
              },
            ]}
          />
          <Flex
            gap={24}
            wrap="wrap"
            justify="center"
            align="center"
            className="full-width"
          >
            <Suspense
              fallback={<SuspenseFallback height={'230px'} width={'230px'} />}
            >
              <Plot
                data={plotData}
                layout={layout}
                config={{ responsive: true, displayModeBar: false }}
              />
            </Suspense>
          </Flex>
        </Flex>
      </Card>
    </Spin>
  );
};

export default React.memo(VehicleStatusChart);
